import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";

/**
 * DESIGN_SYSTEM.md §4/§7 — label above, helper/error below, never a
 * placeholder-as-label (an accessibility failure). Errors are wired via
 * aria-describedby and aria-invalid so screen readers announce them.
 */
interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  help?: ReactNode;
  error?: string;
  /**
   * An action rendered on the label's right — "Forgot password?" beside
   * Password. Kept on the label row rather than below the input so it reads as
   * belonging to that field, and so it does not sit between the input and its
   * error message where it would separate the two.
   */
  labelAction?: ReactNode;
}

export function Field({
  label,
  help,
  error,
  labelAction,
  ...inputProps
}: FieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  return (
    <div className="field">
      {labelAction ? (
        <span className="auth__labelrow">
          <label className="field__label" htmlFor={id}>
            {label}
          </label>
          {labelAction}
        </span>
      ) : (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      )}
      <input
        {...inputProps}
        id={id}
        className="field__input"
        aria-invalid={error ? "true" : undefined}
        aria-describedby={
          [help ? helpId : null, error ? errorId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
      />
      {help && !error && (
        <span className="field__help" id={helpId}>
          {help}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

interface SelectFieldProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "id"
> {
  label: string;
  help?: ReactNode;
  error?: string;
  children: ReactNode;
}

export function SelectField({
  label,
  help,
  error,
  children,
  ...selectProps
}: SelectFieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select
        {...selectProps}
        id={id}
        className="field__select"
        // Same wiring as the text input above. Accepting an `error` prop
        // without associating it with the control means a screen-reader user
        // never hears why the field was rejected.
        aria-invalid={error ? "true" : undefined}
        aria-describedby={
          [help ? helpId : null, error ? errorId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
      >
        {children}
      </select>
      {help && !error && (
        <span className="field__help" id={helpId}>
          {help}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
